import classNames from "classnames";
import React from "react";
import { Link as RLink } from "react-router-dom";

import ElyadataWordmark from "../ElyadataWordmark/ElyadataWordmark";
import "./site-header.scss";

type Props = {
  /** Page links, rendered inside the centre capsule. */
  nav?: React.ReactNode;
  /**
   * Right hand controls: a segmented control and / or a call to action, in the
   * slot elyadata.com uses for its language toggle and its primary button.
   */
  controls?: React.ReactNode;
  className?: string;
};

/**
 * The bar every page of the site wears, built like the elyadata.com header:
 * wordmark on the left, a rounded capsule of links in the centre, controls on
 * the right.
 *
 * Fixed rather than sticky: `#root` carries `overflow: hidden`, which makes it a
 * scroll container that never scrolls, and a `position: sticky` child of such a
 * container simply stays where it was laid out. Pages reserve `--nav-h`.
 */
export default function SiteHeader({ nav, controls, className }: Props) {
  return (
    <header className={classNames("site-header", className)}>
      <div className="site-header__inner">
        <RLink
          to="/"
          className="site-header__home"
          aria-label="Elyadata Radars"
        >
          <ElyadataWordmark className="site-header__logo" />
        </RLink>

        {nav && <nav className="site-header__pill">{nav}</nav>}

        {controls && <div className="site-header__controls">{controls}</div>}
      </div>
    </header>
  );
}

/** The ink pill elyadata.com ends its header with. */
export const SiteHeaderCta = ({
  href,
  children,
}: React.PropsWithChildren<{ href: string }>) => (
  <a
    className="site-header__cta"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
  >
    {children}
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </svg>
  </a>
);
